import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AutocompleteComponent } from '@snapon/design-system-angular';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FormBuilder } from '@angular/forms';
import { DataLayerService } from 'core/data-layer-service/data-layer.service';
import { UtilsService } from 'core/utils/utils.service';
import { SelectedAccountManagerService } from 'core/selected-account-manager/selected-account-manager.service';
import { AccountManager } from '../../shared/entities/account-manager.class';
import { AppLocalization } from '../../shared/entities/app-localization';
import { ConsignedWarehouseService } from '../../pos/quotes/services/consigned-warehouse.service';
import { Warehouse } from '../../shared/entities/warehouse';
import { DashboardService } from './services/dashboard.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild('accountManagerAutocomplete') accountManagerAutocomplete: AutocompleteComponent;
  amEmployeeId: string;
  isAccountManager: boolean;
  isLoading = false;
  appStrings: AppLocalization;
  onlineSyncStatusTemplateValues = {};
  reportsVisible = environment.showReports;
  goOnlineTriggered = false;
  isAdmin: boolean;
  selectedAccountManager: string;
  accountManagersListDisplay: { value: string, label: string }[] = [];
  accountManagersList: AccountManager[] = [];
  warehouseList: Warehouse[] = [];
  isWarehouseListVisible = false;
  consignedReportDisabled = true;

  get clearFiltersDisplayed(): boolean {
    return !!this.amEmployeeId;
  }

  private subs = new Subscription();
  constructor(
    private appStateService: AppStateService,
    private appInfoService: AppInfoService,
    private router: Router,
    private dataLayerService: DataLayerService,
    private dashboardService: DashboardService,
    private consignedWarehouseService: ConsignedWarehouseService,
    private utilsService: UtilsService,
    private ref: ChangeDetectorRef,
    private fb: FormBuilder,
    private selectedAccountManagerService: SelectedAccountManagerService,
  ) { }

  accountManagersLoading = false;
  accountManagerForm = this.fb.group({
    accountManager: [''],
    consignedWarehouse: ['']
  });

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.isAdmin = this.appInfoService.isAdminTypeUser();

    this.isAccountManager = this.appInfoService.isAccountManager();
    if (this.isAccountManager) {
      this.amEmployeeId = this.appInfoService.userData.UserInfo.EmployeeId;
      this.isWarehouseListVisible = true;
      this.loadWarehouses(this.amEmployeeId);
      this.accountManagerForm.controls.accountManager.setValue(this.amEmployeeId);
    }

    if (this.isAdmin) {
      this.getAccountManagers();
    }

    this.subs.add(this.appStateService.triggerIsLoading$.subscribe(loadingState => this.isLoading = loadingState));
    this.dataLayerService.logEvent('homepage-pageview', {
      page_type: 'homepage',
    });
  }

  /**
   * Gets account managers list
   */
  getAccountManagers() {
    this.accountManagersLoading = true;
    this.dashboardService.getAccountManagers((list: AccountManager[]) => {
      this.accountManagersList = list;
      this.accountManagersListDisplay = this.utilsService.mapObjectToSelect(list, 'employeeId', 'name');
      this.accountManagersLoading = false;

      const selectedAccountManager = this.selectedAccountManagerService.selected;
      if (selectedAccountManager) {
        this.amEmployeeId = selectedAccountManager.employeeId;
        this.selectedAccountManager = this.amEmployeeId;
        this.loadWarehouses(this.amEmployeeId);
        this.accountManagerForm.controls.accountManager.setValue(this.amEmployeeId);
        this.ref.detectChanges();
      }
    });
  }

  /**
   * Loads warehouse list for an employee
   * @param employeeId employee id
   */
  loadWarehouses(employeeId: string) {
    this.consignedWarehouseService.getWarehouseListForAM(employeeId).subscribe(warehouses => {
      this.warehouseList = this.utilsService.mapObjectToSelectMultipleLabel(
        warehouses,
        'warehouseCode',
        ['warehouseName', 'warehouseCode']
      );
      this.isWarehouseListVisible = true;
      this.consignedReportDisabled = true;

      if (this.warehouseList.length === 0) {
        this.accountManagerForm.controls.consignedWarehouse.disable();
      } else {
        this.accountManagerForm.controls.consignedWarehouse.enable();
      }
      this.ref.detectChanges();
    });
  }

  /**
   * Gets warehouse report as pdf
   */
  printWarehouseReport() {
    const selectedOrLoggedAM = this.appInfoService.isAccountManager() ? this.amEmployeeId : this.selectedAccountManager;
    this.consignedWarehouseService.getWarehouseReport(this.accountManagerForm.controls.consignedWarehouse.value, selectedOrLoggedAM);
  }

  handleViewReports() {
    this.router.navigate(['/dashboard/reports']);
  }

  /**
   * Account Manager selection changed
   * @param event - The event
   */
  onAccountManagerSelect(event$: MatAutocompleteSelectedEvent) {
    this.accountManagerAutocomplete?.onInput('');
    if (event$.option.value !== this.amEmployeeId) {
      this.amEmployeeId = event$.option.value;
      const accountManager = this.accountManagersList.find((am) => am.employeeId === this.amEmployeeId);
      if (!this.amEmployeeId) {
        return;
      }
      this.selectedAccountManager = this.amEmployeeId;
      this.isWarehouseListVisible = false;
      this.accountManagerForm.controls.consignedWarehouse.setValue(null);
      this.loadWarehouses(this.amEmployeeId);

      this.selectedAccountManagerService.select(accountManager);
    }
  }

  /**
   * Clears the selected Account Manager filter and returns the dashboard to its default state
   */
  clearFilters() {
    this.amEmployeeId = null;
    this.selectedAccountManager = null;
    this.isWarehouseListVisible = false;
    this.warehouseList = [];
    this.consignedReportDisabled = true;
    this.accountManagerForm.setValue({ accountManager: null, consignedWarehouse: null });
    this.accountManagerAutocomplete?.onInput('');

    this.selectedAccountManagerService.clearAll();
    this.ref.detectChanges();
  }

  /**
   * Warehouse selection changed
   * @param event - The event
   */
  onWarehouseSelect($event: { value: any }) {
    if (!!$event.value) {
      this.consignedReportDisabled = false;
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

}
